import React, { useCallback, useState, useEffect, useRef } from "react";
import { bcbfCourseIds, draCourseIds, projectStartDate } from "../config";
import usePromise from "../utils/hooks/usePromise";
import { services } from "../services";
import { Navbar } from "../components/Navbar";
import Select from "react-select";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { getCookieObject } from "../utils/cookie";
import { jwtDecode } from "jwt-decode";

import {
  District,
  Gender,
  OnboardingStatus,
  PaymentRequest,
  State,
  VLE,
} from "@iibf/shared-resources/models/db";
import { File } from "@iibf/shared-resources/models/file";

import DataTable, { TableColumn } from "react-data-table-component";
import {
  IDENTITY_PROOF_CODE,
  IDENTITY_PROOF_NAME,
  QUALIFICATION_CODE,
  QUALIFICATION_NAME,
} from "@iibf/shared-resources/consts";
import { useURLState } from "../utils/url-state";
import {
  ObjectKeyPath,
  ObjectKeyPathValueType,
  setObjectValue,
} from "../utils/func";
import { GetCandidatesRequestInputDTO } from "../utils/custom-dto/get-candidates";
import moment from "moment";
import { UserListActionsFragment } from "./CandidateStats/UserListActionsFragment";
import { downloadFileFromURL } from "../utils/file";
import { MonthYearList } from "./CandidateStats/MonthYearList";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import type { Batch } from "../../../shared/lib/types/dto/batch";
import { any } from "zod";

export type User = {
  id: number;
  salutation: string;
  name: string;
  email?: string;
  mobileNumber?: string;
  gender: string;
  createdAt: string;
  updatedAt: string;
  highestQualification: keyof typeof QUALIFICATION_CODE;
  identityProofType: keyof typeof IDENTITY_PROOF_CODE;
  identityProofNumber: string;
  successfulPaymentRequest: Pick<
    PaymentRequest,
    "id" | "cscTransactionId" | "transactionStatus" | "invoiceUrl"
  >;
  highestQualificationFile: File & { downloadUrl: string };
  identityProofFile: File & { downloadUrl: string };
  candidatePhotoFile: File & { downloadUrl: string };
  candidateSignatureFile: File & { downloadUrl: string };
  district: string;
  state: string;
  vle: Pick<VLE, "id" | "name" | "cscId">;
  dateOfBirth: string;
  addressLine1: string;
  pincode: string;
  batchDetails?: Batch[];
  examAndPaymentStatus?: {
    examStatus: string;
    repaymentStatus: string;
    iibfBatchEndDate: any;
  };
  examCenterCity?: string;
  examCenterState?: string;
  examLanguage?: string;
  partnerId?: Number;
  partner?: {
    name?: string;
  };
  reApplied1OrderId?: string;
  reApplied2OrderId?: string;
  firstRepaymentDate?: string;
  secondRepaymentDate?: string;
};

export type UserOnboardingStats = {
  onboardingCount: number;
  courseCompletionCount: number;
  year: string;
  month: string;
  gender: Gender;
  onboardingStatus: OnboardingStatus;
}[];

type ExportJobStatus = {
  status: "PROCESSING" | "COMPLETED" | "FAILED" | string;
  progress?: number;
  total?: number;
  downloadUrl?: string;
  error?: string | null;
};
const toTitleCase = (str: any) => {
  return str.toLowerCase().replace(/\b\w/g, (char: any) => char.toUpperCase());
};
function generatePartnerUserId(partnerName: any, learnerId: number): string {
  const prefix = partnerName?.substring(0, 4).toUpperCase();

  let learnerIdStr = learnerId.toString();

  if (learnerIdStr.length < 5) {
    learnerIdStr = learnerIdStr.padEnd(5, "0");
  } else if (learnerIdStr.length > 5) {
    learnerIdStr = learnerIdStr.substring(0, 5);
  }

  return `${prefix}${learnerIdStr}`;
}
const formatDate = (dateString: any) => {
  if (!dateString) return "Not Updated";

  const date = new Date(dateString);

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const baseColumns: (TableColumn<User> & {
  id?:
    | keyof User
    | "vleName"
    | "vleId"
    | "stateName"
    | "districtName"
    | "transactionNumber"
    | "batchDetails";
})[] = [
  {
    name: "Learner ID",
    selector: (row) => row?.id,
    width: "8rem",
    wrap: true,
    id: "id",
  },
  {
    name: "Salutation",
    selector: (row) =>
      row?.salutation
        ? row.salutation.charAt(0).toUpperCase() +
          row.salutation.slice(1).toLowerCase()
        : "",
    id: "salutation",
  },
  {
    name: "Name",
    selector: (row) => toTitleCase(row?.name || ""),
    sortable: true,
    width: "14rem",
    wrap: true,
    id: "name",
  },
  {
    name: "Email",
    selector: (row) => row?.email || "",
    sortable: true,
    width: "14rem",
    wrap: true,
    id: "email",
  },
  {
    name: "Mobile Number",
    selector: (row) => row?.mobileNumber || "",
    sortable: true,
    width: "10rem",
    wrap: true,
    id: "mobileNumber",
  },
  {
    name: "Gender",
    selector: (row) => row?.gender,
    sortable: true,
    wrap: true,
    id: "gender",
  },
  {
    name: "DOB",
    selector: (row) => moment(row?.dateOfBirth).format("YYYY-MMM-DD"),
    sortable: true,
    wrap: true,
    id: "dateOfBirth",
    width: "15rem",
  },
  {
    name: "Address",
    selector: (row) => row?.addressLine1,
    sortable: true,
    wrap: true,
    id: "addressLine1",
    width: "20rem",
  },
  {
    name: "Pincode",
    selector: (row) => row?.pincode,
    sortable: true,
    wrap: true,
    id: "pincode",
  },
  {
    name: "Registered At",
    selector: (row) => moment(row?.createdAt).format("DD MMM Y, h:mm:s a"),
    sortable: true,
    width: "12rem",
    wrap: true,
    id: "createdAt",
  },
  {
    name: "Highest Qualification",
    sortable: true,
    selector: (row) => QUALIFICATION_NAME[row?.highestQualification],
    wrap: true,
    id: "highestQualification",
  },
  {
    name: "Identity Proof Type",
    sortable: true,
    selector: (row) => IDENTITY_PROOF_NAME[row?.identityProofType],
    width: "10rem",
    wrap: true,
    id: "identityProofType",
  },
  {
    name: "Identity Proof Number",
    sortable: true,
    selector: (row) => row?.identityProofNumber,
    width: "12rem",
    wrap: true,
    id: "identityProofNumber",
  },
  {
    name: "Registration Transaction Number/Order Id",
    selector: (row) =>
      row?.successfulPaymentRequest?.cscTransactionId
        ? row?.successfulPaymentRequest?.cscTransactionId.toString()
        : !row?.vle?.id
          ? row?.partnerId
            ? generatePartnerUserId(row?.partner, row?.id)
            : row?.successfulPaymentRequest?.id
          : "NA",
    id: "transactionNumber",
    width: "15rem",
  },
  {
    name: "Re-applied 1 Order ID",
    selector: (row) => row.reApplied1OrderId || "NA",
    width: "15rem",
    id: "reApplied1OrderId",
  },
  {
    name: "Re-applied 2 Order ID",
    selector: (row) => row.reApplied2OrderId || "NA",
    width: "15rem",
    id: "reApplied2OrderId",
  },
  {
    name: "First Repayment Date",
    selector: (row) =>
      row?.firstRepaymentDate
        ? moment(row?.firstRepaymentDate).format("DD-MMM-YYYY")
        : "NA",
    width: "12rem",
    id: "firstRepaymentDate",
  },
  {
    name: "Second Repayment Date",
    selector: (row) =>
      row?.secondRepaymentDate
        ? moment(row?.secondRepaymentDate).format("DD-MMM-YYYY")
        : "NA",
    width: "12rem",
    id: "secondRepaymentDate",
  },

  {
    name: "Batch Name",
    sortable: true,
    selector: (row) =>
      row?.batchDetails?.map((batch) => batch.batchName).join(", ") || "",
    width: "15rem",
    id: "batchDetails",
  },
  {
    name: "Batch Start Date",
    sortable: true,
    selector: (row) =>
      row?.batchDetails
        ?.map((batch) => moment(batch?.batchStartDate).format("DD-MMM-YYYY"))
        .join(", ") || "",
    id: "batchDetails",
    width: "10rem",
  },
  {
    name: "Batch End Name",
    sortable: true,
    selector: (row) =>
      row?.batchDetails
        ?.map((batch) => moment(batch?.batchEndDate).format("DD-MMM-YYYY"))
        .join(", ") || "",
    id: "batchDetails",
    width: "10rem",
  },
  {
    name: "Batch Start Time",
    sortable: true,
    selector: (row: any) =>
      row?.batchDetails?.map((batch: any) => batch?.batchStartTime) || "",
    id: "batchDetails",
    width: "10rem",
  },
  {
    name: "Batch End Time",
    sortable: true,
    selector: (row: any) =>
      row?.batchDetails?.map((batch: any) => batch?.batchEndTime) || "",
    id: "batchDetails",
    width: "10rem",
  },
  {
    name: "Seats Left",
    sortable: true,
    selector: (row) =>
      row?.batchDetails
        ?.map(
          (batch) =>
            `${
              batch.max_learners -
              batch?.userEnrollmentBatchArray_aggregate?.aggregate.count
            }`,
        )
        .join(", ") || "",
    wrap: true,
    id: "batchDetails",
  },
  {
    name: "Batch Language",
    sortable: true,
    selector: (row) =>
      row?.batchDetails?.map((batch) => batch.batchLanguage).join(", ") || "",
    wrap: true,
    id: "batchDetails",
    width: "10rem",
  },
  {
    name: "Candidate District",
    sortable: true,
    selector: (row) => row?.district,
    wrap: true,
    id: "districtName",
    width: "10rem",
  },
  {
    name: "Candidate State",
    sortable: true,
    selector: (row) => row?.state,
    wrap: true,
    id: "stateName",
    width: "10rem",
  },
  {
    name: "Exam Center District",
    selector: (row) => row?.examCenterCity || "NA",
    id: "examCenterCity",
    width: "12rem",
  },
  {
    name: "Exam Center State",
    selector: (row) => row?.examCenterState || "NA",
    id: "examCenterState",
    width: "12rem",
  },
  {
    name: "Exam Language",
    selector: (row) => row?.examLanguage || "NA",
    id: "examLanguage",
    width: "12rem",
  },

  {
    name: "IIBF Batch End Date",
    selector: (row) => formatDate(row?.examAndPaymentStatus?.iibfBatchEndDate),
    width: "12rem",
  },
  {
    name: "Candidate Exam Status",
    selector: (row) => row?.examAndPaymentStatus?.examStatus || "NA",
    width: "12rem",
  },
  {
    name: "Exam Repayment Status",
    selector: (row) => row?.examAndPaymentStatus?.repaymentStatus || "NA",
    width: "12rem",
  },
  {
    name: "Actions",
    cell: (row) => <UserListActionsFragment row={row} />,
    grow: 2,
    wrap: true,
    id: "updatedAt",
    width: "65rem",
  },
];

const STATE_ADMIN_ALLOWED_COLUMN_IDS: (keyof User | string)[] = [
  "salutation",
  "name",
  "email",
  "gender",
  "addressLine1",
  "pincode",
  "createdAt",
  "highestQualification",
  "identityProofType",
  "transactionNumber",
  "batchLanguage",
  "districtName",
  "stateName",
];

type Course = {
  value: number;
  label: string;
  batches: { id: number; batch_name: string }[];
};
export default function CandidateStatsPage() {
  const [adminStateName, setAdminStateName] = useState<string | null>(null);
  const [states, setStates] = useState<
    {
      name: string;
      pincodes: string[];
      districts: { name: string; pincodes: string[] }[];
    }[]
  >([]);

  const [districts, setDistricts] = useState<
    { name: string; pincodes: string[] }[]
  >([]);
  console.log("states!", states);
  const [selectedState, setSelectedState] = useState<string>("");
  const [includeFileLinks, setIncludeFileLinks] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState<string>("");
  const [downloadAllData, setDownloadAllData] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);

  const [selectedCourse, setSelectedCourse] = useState<{
    value: number | undefined;
    label: string | null;
  } | null>(null);
  const [batches, setBatches] = useState<{ value: number; label: string }[]>(
    [],
  );
  const [examStatus, setExamStatus] = useState<
    { value: string; label: string }[]
  >([
    { value: "fail", label: "Fail" },
    { value: "pass", label: "Pass" },
    { value: "not_appeared", label: "Not Appeared" },
    { value: "ineligible", label: "Ineligible" },
  ]);
  const [rePaymentStatus, setRepaymentStatus] = useState<
    { value: number; label: string }[]
  >([
    { value: 0, label: "Not Paid" },
    { value: 1, label: "Re-applied 1" },
    { value: 2, label: "Re-applied 2" },
  ]);
  const [selectedGender, setSelectedGender] = useState<string>("");
  const [selectedExamStatus, setSelectedExamStatus] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedRepaymentStatus, setSelectedRepaymentStatus] = useState<
    { value: number; label: string }[]
  >([]);
  const [selectedBatches, setSelectedBatches] = useState<
    { value: number; label: string }[]
  >([]);
  const [selectedTab, setSelectedTab] = useState<
    "total" | "vle" | "self" | "partner_bulk_onboarded"
  >("total");
  const [includePartner, setIncludePartner] = useState(false);
  const [exportStatus, setExportStatus] = useState<ExportJobStatus | null>(
    null,
  );
  const [exportJobId, setExportJobId] = useState<string | null>(null);
  const [targetProgressPct, setTargetProgressPct] = useState(0);
  const [displayProgressPct, setDisplayProgressPct] = useState(0);
  const location = useLocation();
  const downloadInFlightRef = useRef(false);
  const [role, setRole] = useState<any>(null);

  const { data: searchParams, setValue: setSearchParams } = useURLState(
    {
      path: "searchParams",
      schema: GetCandidatesRequestInputDTO,
      initValue: {
        filter: {
          registrationStartDate: new Date(projectStartDate),
          registrationEndDate: new Date(),
        },
        sort: [],
        paginationParams: {
          pageIndex: 0,
          pageSize: 10,
        },
      },
      onError: (err) => {
        console.error(err);
        alert("An error occurred while parsing search parameters");
      },
    },
    [],
  );

  const { filter, sort: sortParams, paginationParams } = searchParams || {};

  const setSearchParam = useCallback(
    <OKP extends ObjectKeyPath<typeof searchParams>>(
      changes: [OKP, ObjectKeyPathValueType<typeof searchParams, OKP>][],
    ) => {
      if (searchParams instanceof Object) {
        for (const change of changes) {
          setObjectValue(searchParams, change[0], change[1]);
        }
        setSearchParams({ ...searchParams });
      }
    },
    [searchParams, setSearchParams],
  );
  const [searchKeyword, setSearchKeyword] = useState(filter?.keywords || "");

  const {
    result: searchUsersRes,
    error: searchUsersErr,
    loading: searchingUsers,
  } = usePromise(
    {
      fn: async () => {
        const adminStatesArr = adminStateName ? adminStateName.split(",").map(x => x.trim().toLowerCase()) : [];
        const stateAdminStates = states.filter((s) => adminStatesArr.includes(s.name.toLowerCase()));

        const districtLevelPincodes =
          searchParams?.filter?.pincodes &&
          searchParams.filter.pincodes.length > 0
            ? searchParams.filter.pincodes
            : null;

        const isStateAdminUser = role?.role === "STATE_ADMIN";
        
        // If state admin hasn't selected a specific state filter yet, use all their assigned pincodes.
        const allAdminPincodes = stateAdminStates.flatMap(s => s.pincodes);
        const finalPincodes = isStateAdminUser
          ? (districtLevelPincodes ?? (allAdminPincodes.length > 0 ? allAdminPincodes : null))
          : searchParams?.filter?.pincodes;
        return await services.requestSvc.post<{
          data: User[];
          pagination: {
            currentPageIndex: number;
            pageSize: number;
            totalUsersCount: number;
            totalPages: number;
          };
        }>("/users", {
          ...searchParams,
          filter: {
            ...searchParams?.filter,
            pincodes: finalPincodes,
          },
          userType:
            selectedTab === "total"
              ? includePartner
                ? "ALL"
                : "TOTAL_NO_PARTNER"
              : selectedTab === "vle"
                ? "VLE"
                : selectedTab === "partner_bulk_onboarded"
                  ? "BULK_ONBOARDED"
                  : "SELF",
          courseIds:
            location.pathname == "/dra-stats" ? draCourseIds : bcbfCourseIds,
        });
      },
      runOnMount: { args: [] },
      runOnRerender: { enabled: true, args: [] },
    },
    [searchParams, location.pathname, selectedTab, includePartner, role, adminStateName, states],
  );

  const usersData = searchUsersRes?.data;
  const userCount = searchUsersRes?.pagination.totalUsersCount;
  console.log(role, "rooo");
  const { run: downloadCsv, loading: preparingCsv } = usePromise(
    {
      fn: async () => {
        if (!searchParams) throw new Error("Search Params not set");

        if (downloadInFlightRef.current) {
          toast.info("Report is already being prepared.");
          return;
        }

        downloadInFlightRef.current = true;
        setExportStatus({ status: "PROCESSING", progress: 0, total: 0 });
        setExportJobId(null);

        try {
          const now = moment();

          const downloadFilter = {
            ...searchParams.filter,
            registrationStartDate: downloadAllData
              ? searchParams.filter.registrationStartDate
              : now.clone().startOf("month").toDate(),

            registrationEndDate: downloadAllData
              ? searchParams.filter.registrationEndDate
              : now.clone().endOf("day").toDate(),
          };

          const res = await services.requestSvc.post<{
            jobId: string;
            status: string;
          }>("/download-candidate-report", {
            ...downloadFilter,
            courseIds:
              location.pathname == "/dra-stats" ? draCourseIds : bcbfCourseIds,

            userType:
              selectedTab === "total"
                ? includePartner
                  ? "ALL"
                  : "TOTAL_NO_PARTNER"
                : selectedTab === "vle"
                  ? "VLE"
                  : selectedTab === "partner_bulk_onboarded"
                    ? "BULK_ONBOARDED"
                    : "SELF",

            includeFileLinks,
            downloadAllData,
          });

          toast.info("Report is being prepared...");

          const jobId = res.jobId;
          console.log(jobId, "jobId");
          setExportJobId(jobId);

          const maxPollDurationMs = 15 * 60 * 1000;
          const maxErrors = 3;
          let errorCount = 0;
          const pollIntervalMs = 3000;
          const startTime = Date.now();

          const poll = async () => {
            if (Date.now() - startTime > maxPollDurationMs) {
              toast.error("Export is taking too long. Please try again later.");
              setExportStatus(null);
              setExportJobId(null);
              downloadInFlightRef.current = false;
              return;
            }

            try {
              const statusRes = await services.requestSvc.get(
                `/export-status/${jobId}`,
              );

              console.log("statusResp", statusRes);
              setExportStatus(statusRes);
              if (statusRes?.status === "COMPLETED") {
                // toast.success("Report ready! Downloading now...");

                downloadFileFromURL(
                  statusRes?.downloadUrl!,
                  `candidate-report-${jobId}.csv`,
                );
                downloadInFlightRef.current = false;
                setTimeout(() => {
                  setExportStatus(null);
                  setExportJobId(null);
                }, 3000);
                return;
              }

              if (statusRes?.status === "FAILED") {
                toast.error("Export Failed");
                downloadInFlightRef.current = false;
                setTimeout(() => {
                  setExportStatus(null);
                  setExportJobId(null);
                }, 3000);
                return;
              }

              errorCount = 0;
            } catch (err) {
              errorCount += 1;
              if (errorCount >= maxErrors) {
                toast.error(
                  "Unable to check export status. Please try again later.",
                );
                setExportStatus(null);
                setExportJobId(null);
                downloadInFlightRef.current = false;
                return;
              }
            }

            setTimeout(poll, pollIntervalMs);
          };

          poll();
        } catch (err) {
          setExportStatus(null);
          setExportJobId(null);
          downloadInFlightRef.current = false;
          throw err;
        }
      },
    },
    [
      searchParams,
      location.pathname,
      selectedTab,
      includePartner,
      includeFileLinks,
      downloadAllData,
      exportStatus,
    ],
  );
  useEffect(() => {
    const { access_token: accessToken } = getCookieObject();
    if (accessToken) {
      try {
        const decoded = jwtDecode(accessToken);
        console.log("decoded", decoded);
        setRole(decoded);
      } catch (err) {
        console.error("Invalid token", err);
      }
    }
  }, []);

  const isExportInProgress =
    preparingCsv || exportStatus?.status === "PROCESSING";

  useEffect(() => {
    if (!exportStatus) {
      setTargetProgressPct(0);
      setDisplayProgressPct(0);
      return;
    }

    if (exportStatus.status === "COMPLETED") {
      setTargetProgressPct(100);
      setDisplayProgressPct(100);
      return;
    }

    if (
      typeof exportStatus.progress === "number" &&
      typeof exportStatus.total === "number" &&
      exportStatus.total > 0
    ) {
      const nextTarget = Math.min(
        100,
        (exportStatus.progress / exportStatus.total) * 100,
      );
      setTargetProgressPct(nextTarget);
      setDisplayProgressPct((prev) => (prev > nextTarget ? nextTarget : prev));
    }
  }, [exportStatus]);

  useEffect(() => {
    if (!exportStatus || exportStatus.status !== "PROCESSING") return;

    const id = setInterval(() => {
      setDisplayProgressPct((prev) => {
        let currentTarget = targetProgressPct;
        
        if (currentTarget <= 0) {
           if (prev >= 90) return prev;
           return prev + Math.max(0.1, (90 - prev) * 0.05);
        }
        
        if (prev >= currentTarget) return prev;
        const gap = currentTarget - prev;
        const step = Math.max(0.5, gap * 0.2);
        const next = Math.min(currentTarget, prev + step);
        return Math.min(next, 99);
      });
    }, 300);

    return () => clearInterval(id);
  }, [exportStatus, targetProgressPct]);

  useEffect(() => {
    services.requestSvc

      .get<{
        states: {
          name: string;
          pincodes: string[];
          districts: { name: string; pincodes: string[] }[];
        }[];
      }>("/get-states-district-pincode-info")
      .then((response) => {
        setStates(response.states);
      })
      .catch((error) => console.error(error));
  }, []);

  useEffect(() => {
    if (!role?.role) return;

    services.requestSvc
      .post<{ stateName: string | null }>("/admin/state-context", {
        role: role?.role,
        actorId: role?.actorId,
      })
      .then((res) => {
        if (res) {
          setAdminStateName(res?.stateName);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch admin state context", err);
      });
  }, [role]);

  useEffect(() => {
    setSelectedTab("vle");
  }, [role]);

  useEffect(() => {
    if (filter?.genders && filter.genders.length > 0) {
      setSelectedGender(filter.genders[0]);
    } else {
      setSelectedGender("");
    }
  }, [filter?.genders]);

  useEffect(() => {
    const courseIds =
      location.pathname === "/dra-stats" ? draCourseIds : bcbfCourseIds;
    services.requestSvc
      .post<{
        success: boolean;
        data: {
          id: number;
          full_name: string;
          batches: { id: number; batch_name: string }[];
        }[];
        message?: string;
      }>("/get-iibf-course-batches", {
        course_ids: courseIds,
      })
      .then((response) => {
        if (response.success) {
          const courseBatchOptionsInfo = response.data.map((course) => ({
            value: course.id,
            label: course.full_name,
            batches: course.batches,
          }));
          setCourses(courseBatchOptionsInfo);
          const allBatches = response.data.flatMap((course) =>
            course.batches.map((batch) => ({
              value: batch.id,
              label: batch.batch_name,
            })),
          );
          setBatches(allBatches);
        } else {
          toast.error(
            "Failed to fetch courses and batches: " + response.message,
          );
        }
      })
      .catch((error: Error) => {
        toast.error("Something went wrong while fetching batches.");
      });
  }, [location.pathname]);

  const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const stateName = event.target.value;
    setSelectedState(stateName);
    setSelectedDistrict("");

    const selectedStateObj = states?.find((s) => s.name === stateName);
    const pincodes = selectedStateObj ? (selectedStateObj.pincodes.length > 0 ? selectedStateObj.pincodes : ["INVALID_PINCODE"]) : [];
    const districtsFromState = selectedStateObj?.districts || [];

    setDistricts(districtsFromState);

    setSearchParam([[["filter", "pincodes"], pincodes]]);
  };

  const handleCourseChange = (
    selected: { value: number | undefined; label: string | null } | null,
  ) => {
    setSelectedCourse(selected);
    setSelectedBatches([]);
    setSearchParam([[["filter", "batchIds"], []]]);
    setSearchParam([
      [["filter", "courseId"], selected ? selected.value : undefined],
    ]);
    setSearchParam([[["paginationParams", "pageIndex"], 0]]);
  };
  const handleBatchChange = (
    selected: readonly { value: number; label: string }[],
  ) => {
    const batchIds = selected.map((batch) => batch.value);
    setSelectedBatches([...selected]);
    setSearchParam([[["filter", "courseId"], []]]);
    setSearchParam([[["filter", "batchIds"], batchIds]]);
    setSearchParam([[["paginationParams", "pageIndex"], 0]]);
  };
  const handleExamStatusChange = (
    selected: readonly { value: any; label: string }[],
  ) => {
    const examStatus = selected.map((status) => status.value);
    setSelectedExamStatus([...selected]);
    setSearchParam([[["filter", "examStatus"], examStatus]]);
    setSearchParam([[["paginationParams", "pageIndex"], 0]]);
  };
  const handleRepaymentStatusChange = (
    selected: readonly { value: any; label: string }[],
  ) => {
    const repaymentStatus = selected.map((status) => status.value);
    console.log(
      "repaymentStatusType",
      selected.map((status) => status.value),
    );
    setSelectedRepaymentStatus([...selected]);
    setSearchParam([[["filter", "repaymentStatus"], repaymentStatus]]);
    setSearchParam([[["paginationParams", "pageIndex"], 0]]);
  };
  const getFilteredBatches = () => {
    if (selectedCourse && selectedCourse.value !== undefined) {
      return (
        courses
          .find((course) => course.value === selectedCourse.value)
          ?.batches.map((batch) => ({
            value: batch.id,
            label: batch.batch_name,
          })) || []
      );
    } else {
      return batches;
    }
  };
  const getExamStatus = () => {
    return (
      examStatus.map((status: any) => ({
        value: status.value,
        label: status.label,
      })) || []
    );
  };
  const getRepaymentStatus = () => {
    return (
      rePaymentStatus.map((status: any) => ({
        value: status.value,
        label: status.label,
      })) || []
    );
  };
  const handleDistrictChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const districtName = event.target.value;
    setSelectedDistrict(districtName);

    const selectedDistrictObj = districts.find((d) => d.name === districtName);
    const pincodes = selectedDistrictObj?.pincodes[0] || [];

    setSearchParam([[["filter", "pincodes"], [pincodes]]]);
  };
  const handleGenderChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const gender = event.target.value;
    setSelectedGender(gender);
    setSearchParam([
      [["filter", "genders"], gender ? [gender] : []],
      [["paginationParams", "pageIndex"], 0],
    ]);
  };
  const isStateAdmin = role?.role === "STATE_ADMIN";
  const finalColumns = baseColumns.filter((col) => {
    if (isStateAdmin) {
      return STATE_ADMIN_ALLOWED_COLUMN_IDS.includes(col.id as string);
    }

    if (
      ["examCenterCity", "examCenterState", "examLanguage"].includes(
        col.id as string,
      ) &&
      (location.pathname !== "/dra-stats" ||
        selectedTab === "partner_bulk_onboarded")
    ) {
      return false;
    }

    if (
      ["Candidate Exam Status", "Exam Repayment Status"].includes(
        col.name as string,
      ) &&
      location.pathname !== "/dra-stats"
    ) {
      return false;
    }

    if (
      ["IIBF Batch End Date"].includes(col.name as string) &&
      location.pathname !== "/dra-stats"
    ) {
      return false;
    }

    if (
      ["Re-applied 1 Order ID", "Re-applied 2 Order ID"].includes(
        col.name as string,
      ) &&
      location.pathname !== "/dra-stats"
    ) {
      return false;
    }

    if (
      ["firstRepaymentDate", "secondRepaymentDate"].includes(
        col.id as string,
      ) &&
      location.pathname !== "/dra-stats"
    ) {
      return false;
    }

    return true;
  });

  useEffect(() => {
    // We remove the force-selection hook.
    // If the admin wants to filter down to 1 state, they can use the dropdown manually.
  }, [isStateAdmin]);

  return (
    <div className="flex flex-col p-4 w-full">
      {/* <div className="flex flex-wrap gap-2 mt-6">
        {["total", "vle", "self", "partner_bulk_onboarded"].map((tab) => (
          <button
            key={tab}
            onClick={() => setSelectedTab(tab as any)}
            className={`px-4 py-2 rounded font-semibold w-full sm:w-auto text-center ${
              selectedTab === tab
                ? "bg-[#001D67] text-white"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            {tab === "total"
              ? "Total"
              : tab === "vle"
              ? "Onboarded by VLE"
              : tab === "partner_bulk_onboarded"
              ? "Partner Bulk Onboarded"
              : "Self Onboarded"}
          </button>
        ))}
      </div> */}
      {!isStateAdmin && (
        <>
          <div className="flex flex-wrap gap-2 mt-6">
            <div className="flex flex-wrap gap-2 border border-blue rounded p-1">
              {["total", "vle", "self"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setSelectedTab(tab as any)}
                  className={`px-4 py-2 rounded font-semibold w-full sm:w-auto text-center ${
                    selectedTab === tab
                      ? "bg-[#001D67] text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {tab === "total"
                    ? "Total"
                    : tab === "vle"
                      ? "Onboarded by VLE"
                      : "Self Onboarded"}
                </button>
              ))}
            </div>

            <button
              onClick={() => setSelectedTab("partner_bulk_onboarded")}
              className={`px-4 py-2 rounded font-semibold w-full sm:w-auto text-center ml-4 ${
                selectedTab === "partner_bulk_onboarded"
                  ? "bg-[#001D67] text-white"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              Partner Bulk Onboarded
            </button>
          </div>

          {selectedTab === "total" && (
            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={includePartner}
                onChange={(e) => setIncludePartner(e.target.checked)}
              />
              <label className="text-sm">
                Include Partner Bulk Onboarded Learners
              </label>
            </div>
          )}
        </>
      )}
      <div className="flex flex-wrap justify-between items-center rounded-lg mb-6 mt-4 w-full">
        <div className="font-semibold text-2xl mt-4">Candidate Report</div>
        <div className="flex flex-wrap justify-between items-center rounded-lg mb-6 w-full">
          <div className="text-lg font-semibold flex items-center gap-x-3">
            Total registered learners:
            <span className="text-3xl text-blue">{userCount}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 mb-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex flex-col sm:flex-row sm:space-x-4 gap-2 w-full">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 w-full sm:w-auto">
              <label className="font-semibold">From: </label>
              <input
                type="date"
                value={moment(filter?.registrationStartDate).format("Y-MM-DD")}
                onChange={(e) =>
                  setSearchParam([
                    [
                      ["filter", "registrationStartDate"],
                      new Date(e.target.value),
                    ],
                  ])
                }
                className="border rounded px-4 py-2 w-full sm:w-auto"
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 w-full sm:w-auto">
              <label className="font-semibold">To:</label>
              <input
                type="date"
                value={moment(filter?.registrationEndDate).format("Y-MM-DD")}
                onChange={(e) =>
                  setSearchParam([
                    [
                      ["filter", "registrationEndDate"],
                      moment(e.target.value).endOf("day").toDate(),
                    ],
                  ])
                }
                className="border rounded px-4 py-2 w-full sm:w-auto"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
            <span className="font-semibold">Search:</span>
            <input
              type="text"
              defaultValue={filter?.keywords}
              id="keywords"
              placeholder="Enter keywords"
              className="border rounded px-4 py-2 w-full sm:w-80"
              onChange={(e) => setSearchKeyword(e.target.value)}
            />
            <button
              onClick={() =>
                setSearchParam([[["filter", "keywords"], searchKeyword]])
              }
              className="btn-primary"
            >
              Search
            </button>
          </div>
        </div>
        {((filter?.registrationEndDate && filter?.registrationStartDate) ||
          (filter?.repaymentStartDate && filter?.repaymentEndDate)) && (
          <div className="flex flex-col gap-2">
            <span className="font-semibold">Quick select:</span>
            <div className="flex flex-wrap gap-3">
              <MonthYearList
                startDate={new Date(projectStartDate)}
                endDate={new Date()}
                selectedDateRangeStart={
                  selectedRepaymentStatus.length > 0
                    ? searchParams?.filter.repaymentStartDate
                    : searchParams?.filter.registrationStartDate
                }
                selectedDateRangeEnd={
                  selectedRepaymentStatus.length > 0
                    ? searchParams?.filter.repaymentEndDate
                    : searchParams?.filter.registrationEndDate
                }
                onChange={(startDate, endDate) => {
                  if (selectedRepaymentStatus.length > 0) {
                    setSearchParam([
                      [["filter", "repaymentStartDate"], startDate],
                      [["filter", "repaymentEndDate"], endDate],
                      [["filter", "registrationStartDate"], undefined],
                      [["filter", "registrationEndDate"], undefined],
                    ]);
                  } else {
                    setSearchParam([
                      [["filter", "registrationStartDate"], startDate],
                      [["filter", "registrationEndDate"], endDate],
                      [["filter", "repaymentStartDate"], undefined],
                      [["filter", "repaymentEndDate"], undefined],
                    ]);
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-1/2">
            <Select
              options={[
                { value: undefined, label: "Select a Course" },
                ...courses.map((course) => ({
                  value: course.value,
                  label: course.label,
                })),
              ]}
              value={selectedCourse}
              onChange={handleCourseChange}
              getOptionLabel={(e) => e.label || ""}
              getOptionValue={(e) =>
                e.value === undefined ? "" : e.value.toString()
              }
              placeholder="Select a course"
              className="w-full"
            />
          </div>
          <div className="w-full md:w-1/2">
            <Select
              isMulti
              options={getFilteredBatches()}
              value={selectedBatches}
              onChange={handleBatchChange}
              getOptionLabel={(e) => e.label}
              getOptionValue={(e) => e.value.toString()}
              placeholder="Select batches"
              closeMenuOnSelect={false}
              className="w-full"
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <select
            value={selectedState}
            onChange={handleStateChange}
            className="border p-2 rounded w-full sm:w-auto"
          >
            <option value="">{isStateAdmin ? "All My States" : "Select State"}</option>
            {states
               ?.filter((state) => {
                 if (!isStateAdmin) return true;
                 const adminStatesArr = adminStateName ? adminStateName.split(",").map(x => x.trim().toLowerCase()) : [];
                 return adminStatesArr.includes(state.name.toLowerCase());
               })
              .map((state) => (
              <option key={state.name} value={state.name}>
                {state.name}
              </option>
            ))}
          </select>

          <select
            value={selectedDistrict}
            onChange={handleDistrictChange}
            className="border p-2 rounded w-full sm:w-auto"
            disabled={!selectedState}
          >
            <option value="">Select District</option>
            {districts.map((district) => (
              <option key={district.name} value={district.name}>
                {district.name}
              </option>
            ))}
          </select>

          <select
            value={selectedGender}
            onChange={handleGenderChange}
            className="border p-2 rounded w-full sm:w-auto"
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          {location.pathname == "/dra-stats" && !isStateAdmin && (
            <>
              <div className="w-full md:w-1/2">
                <Select
                  isMulti
                  options={getExamStatus()}
                  value={selectedExamStatus}
                  onChange={handleExamStatusChange}
                  getOptionLabel={(e) => e.label}
                  getOptionValue={(e) => e.value.toString()}
                  placeholder="Select Candidate Exam Status"
                  closeMenuOnSelect={false}
                  className="w-full"
                />
              </div>
              <div className="w-full md:w-1/2">
                <Select
                  isMulti
                  options={getRepaymentStatus()}
                  value={selectedRepaymentStatus}
                  onChange={handleRepaymentStatusChange}
                  getOptionLabel={(e) => e.label}
                  getOptionValue={(e) => e.value.toString()}
                  placeholder="Candidate Exam Repayment Status"
                  closeMenuOnSelect={false}
                  className="w-full"
                />
              </div>
            </>
          )}
        </div>
        {!isStateAdmin && (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeFileLinks}
              onChange={(e) => setIncludeFileLinks(e.target.checked)}
            />
            Include document download links in report
          </label>
        )}

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={downloadAllData}
            onChange={(e) => setDownloadAllData(e.target.checked)}
          />
          Download all data
        </label>
        <button
          onClick={downloadCsv}
          className="btn-primary bg-blue-600 text-white px-4 py-2 rounded w-1/4 sm:w-1/4"
          disabled={isExportInProgress}
        >
          {isExportInProgress ? "Preparing CSV..." : "Download CSV"}
        </button>
        {exportStatus && (
          <div className="text-sm text-gray-700">
            Report status:{" "}
            <span className="font-semibold">{exportStatus.status}</span>
            {typeof exportStatus.progress === "number" &&
              typeof exportStatus.total === "number" && (
                <>
                  {" "}
                  ({Math.min(100, Math.floor(displayProgressPct))}%)
                </>
              )}
            {exportJobId && (
              <span className="ml-2 text-gray-500">Job: {exportJobId}</span>
            )}
          </div>
        )}
      </div>
      <div className="mt-6 overflow-x-auto">
        <DataTable
          columns={finalColumns}
          // defaultSortFieldId={sortParams?.[0]?.column}
          defaultSortFieldId="id"
          defaultSortAsc={false}
          progressPending={searchingUsers}
          data={usersData || []}
          paginationDefaultPage={(paginationParams?.pageIndex || 0) + 1}
          pagination
          paginationServer
          paginationRowsPerPageOptions={[2, 5, 10, 15, 30, 60]}
          paginationTotalRows={userCount}
          paginationPerPage={paginationParams?.pageSize}
          onChangePage={(targetPageNumber) =>
            setSearchParam([
              [["paginationParams", "pageIndex"], targetPageNumber - 1],
            ])
          }
          onChangeRowsPerPage={(newCount) => {
            setSearchParam([
              [["paginationParams", "pageIndex"], 0],
              [["paginationParams", "pageSize"], newCount],
            ]);
          }}
          sortServer
          onSort={(column, order) => {
            setSearchParam([[["sort"], [{ column: column.id, order }]]]);
          }}
          customStyles={{
            table: {
              style: {
                overflow: "hidden",
                borderRadius: "8px 8px 0px 0px",
                border: "1px solid rgba(0,0,0,0.4)",
                borderLeft: "0",
              },
            },
            headRow: {
              style: {
                background: "#b1c7e9",
                color: "#3D3D3D",
                fontWeight: "600",
                "&:hover": {
                  cursor: "pointer",
                },
              },
            },
            headCells: {
              style: {
                border: "1px solid rgba(0,0,0,0.4)",
                borderTop: "0",
                borderRight: "0",
                borderBottom: "0",
              },
            },
            cells: {
              style: {
                borderLeft: "1px solid rgba(0,0,0,0.4)",
              },
            },
            rows: {
              style: {
                backgroundColor: "#fff",
                cursor: "default",
                color: "#656363",
                fontSize: "14px",
                "&:hover": {
                  cursor: "default",
                  backgroundColor: "#E9E9E9",
                },
                borderBottom: "1px solid rgba(0,0,0,0.4)",
              },
            },
            pagination: {
              style: {
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                borderRadius: "0.375rem",
                borderTopLeftRadius: "0px",
                borderTopRightRadius: "0px",
                border: "1px solid rgba(0,0,0,0.4)",
                borderTop: "0",
                fontWeight: "500",
                color: "rgb(61, 61, 61)",
              },
            },
          }}
        />
      </div>
    </div>
  );
}
